import {
	AdditiveBlending,
	BackSide,
	BufferGeometry,
	CanvasTexture,
	Color,
	DataTexture,
	DoubleSide,
	Float32BufferAttribute,
	Group,
	Mesh,
	MeshBasicMaterial,
	LinearFilter,
	LinearMipmapLinearFilter,
	PlaneGeometry,
	Points,
	PointsMaterial,
	SRGBColorSpace,
	SphereGeometry,
	RepeatWrapping,
	Vector3,
} from 'three';

const STAR_COUNT = 10_000;
const STAR_RADIUS = 85_000;
const SKY_RADIUS = 92_000;
const NEBULA_RADIUS = 50_000;
const NEBULA_SETTINGS = {
	// Tune these first. Higher alpha = more visible; higher threshold = wispier.
	desktop: {
		alpha: 150,
		verticalScale: 4.8,
		detailScale: 1.35,
		threshold: 0.25,
		power: 1.08,
		color: { r: 74, g: 118, b: 230 },
		colorVariation: { r: 42, g: 52, b: 25 },
		materialColor: 0x91bdff,
	},
	xr: {
		alpha: 180,
		verticalScale: 2.8,
		detailScale: 1.1,
		threshold: 0.25,
		power: 1.08,
		color: { r: 82, g: 128, b: 238 },
		colorVariation: { r: 42, g: 52, b: 25 },
		materialColor: 0xa8cfff,
	},
};

function seededRandom(seed) {
	let state = seed >>> 0;
	return () => {
		state = (state * 1664525 + 1013904223) >>> 0;
		return state / 0x100000000;
	};
}

function createStarField() {
	const random = seededRandom(0x5a17c0de);
	const positions = [];
	const colors = [];
	const color = new Color();

	for (let i = 0; i < STAR_COUNT; i++) {
		const z = random() * 2 - 1;
		const theta = random() * Math.PI * 2;
		const r = Math.sqrt(1 - z * z) * STAR_RADIUS;
		const brightness = 0.45 + Math.pow(random(), 5) * 1.4;
		const temperature = random();

		positions.push(
			Math.cos(theta) * r,
			z * STAR_RADIUS,
			Math.sin(theta) * r,
		);

		color.setRGB(
			brightness * (0.78 + temperature * 0.22),
			brightness * (0.82 + temperature * 0.14),
			brightness * (0.95 + (1 - temperature) * 0.18),
		);
		colors.push(color.r, color.g, color.b);
	}

	const geometry = new BufferGeometry();
	geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
	geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));

	const material = new PointsMaterial({
		size: 1.35,
		sizeAttenuation: false,
		vertexColors: true,
		transparent: true,
		opacity: 0.95,
		depthWrite: false,
	});

	const stars = new Points(geometry, material);
	stars.name = 'SPACE_STARS';
	stars.frustumCulled = false;
	return stars;
}

function createSkyTexture() {
	const random = seededRandom(0x51c0ffee);
	const canvas = document.createElement('canvas');
	canvas.width = 2048;
	canvas.height = 1024;
	const ctx = canvas.getContext('2d');
	ctx.fillStyle = '#01020a';
	ctx.fillRect(0, 0, canvas.width, canvas.height);
	ctx.globalCompositeOperation = 'lighter';

	for (let i = 0; i < 900; i++) {
		const x = random() * canvas.width;
		const y = random() * canvas.height;
		const size = random() < 0.96 ? 1 : 2;
		const alpha = 0.18 + Math.pow(random(), 5) * 0.82;
		const blue = Math.floor(210 + random() * 45);
		ctx.fillStyle = `rgba(${blue}, ${blue}, 255, ${alpha})`;
		ctx.fillRect(x, y, size, size);
	}

	const texture = new CanvasTexture(canvas);
	texture.colorSpace = SRGBColorSpace;
	texture.needsUpdate = true;
	return texture;
}

function createSkySphere() {
	const geometry = new SphereGeometry(SKY_RADIUS, 48, 24);
	const material = new MeshBasicMaterial({
		map: createSkyTexture(),
		side: BackSide,
		depthWrite: false,
	});
	const mesh = new Mesh(geometry, material);
	mesh.name = 'SPACE_SKY_SPHERE';
	mesh.frustumCulled = false;
	return mesh;
}

// Pixels are generated in /assets/nebula-worker.js, one texture at a time (a single worker
// serialises the jobs: keeps CPU use low and spreads the GPU uploads over several frames).
let nebulaWorker = null;
let nebulaChain = Promise.resolve();
function generateNebulaPixels(seed, width, height, settings) {
	if (!nebulaWorker) nebulaWorker = new Worker('/assets/nebula-worker.js');
	const result = nebulaChain.catch(() => {}).then(() => new Promise((resolve, reject) => {
		nebulaWorker.onmessage = ({ data }) => resolve(data);
		nebulaWorker.onerror = reject;
		nebulaWorker.postMessage({ seed, width, height, settings });
	}));
	nebulaChain = result;
	return result;
}

async function createNebulaTexture(seed, quality = 'desktop') {
	const settings = NEBULA_SETTINGS[quality] || NEBULA_SETTINGS.desktop;
	const width = quality === 'xr' ? 2048 : 4096;
	const height = 2048;
	const { buffer } = await generateNebulaPixels(seed, width, height, settings);
	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	canvas.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(buffer), width, height), 0, 0);

	const texture = new CanvasTexture(canvas);
	texture.colorSpace = SRGBColorSpace;
	texture.wrapS = RepeatWrapping;
	texture.wrapT = RepeatWrapping;
	texture.minFilter = LinearMipmapLinearFilter;
	texture.magFilter = LinearFilter;
	texture.generateMipmaps = true;
	texture.anisotropy = 4;
	texture.needsUpdate = true;
	return texture;
}

const NEBULA_PLACEHOLDER = new DataTexture(new Uint8Array([0, 0, 0, 0]), 1, 1);
NEBULA_PLACEHOLDER.needsUpdate = true;

function createNebulaPlane(index, seed, direction, rotationZ, width, height, opacity, quality = 'desktop') {
	const settings = NEBULA_SETTINGS[quality] || NEBULA_SETTINGS.desktop;
	const geometry = new PlaneGeometry(width, height);
	const material = new MeshBasicMaterial({
		map: NEBULA_PLACEHOLDER, // fully transparent until the worker delivers the real texture
		color: settings.materialColor,
		transparent: true,
		opacity,
		blending: AdditiveBlending,
		depthWrite: false,
		depthTest: true,
		side: DoubleSide,
	});

	createNebulaTexture(seed, quality).then(texture => { material.map = texture; });

	const mesh = new Mesh(geometry, material);
	mesh.name = `SPACE_NEBULA_${index}`;
	mesh.userData.spaceNebula = true;
	mesh.userData.nebulaMode = quality;
	mesh.userData.baseOpacity = opacity;
	mesh.position.copy(direction.clone().normalize().multiplyScalar(NEBULA_RADIUS));
	mesh.lookAt(0, 0, 0);
	mesh.rotateZ(rotationZ);
	mesh.frustumCulled = false;
	return mesh;
}

export function createSpaceBackground() {
	const group = new Group();
	group.name = 'SPACE_BACKGROUND';
	group.add(createSkySphere());
	group.add(createStarField());
	group.add(createNebulaPlane(
		0,
		0x7e841,
		new Vector3(0, -0.86, -0.5),
		-0.08,
		160000,
		120000,
		0.95,
	));
	group.add(createNebulaPlane(
		10,
		0x81f31,
		new Vector3(0, -0.86, -0.5),
		-0.08,
		180000,
		135000,
		0.72,
		'xr',
	));
	group.add(createNebulaPlane(
		11,
		0x9a743,
		new Vector3(-0.18, -0.84, -0.51),
		0.16,
		165000,
		125000,
		0.42,
		'xr',
	));
	group.add(createNebulaPlane(
		12,
		0xbee51,
		new Vector3(0.2, -0.83, -0.52),
		-0.26,
		165000,
		125000,
		0.42,
		'xr',
	));
	/*group.add(createNebulaPlane(
		1,
		0xa3471,
		new Vector3(-0.58, 0.24, -0.78),
		0.24,
		135000,
		41000,
		0.62,
	));
	group.add(createNebulaPlane(
		2,
		0x41bb2,
		new Vector3(0.74, -0.18, -0.65),
		-0.34,
		120000,
		36000,
		0.48,
	));*/

	return group;
}
